// The seasonal lay-up switch on the Alarms page (ADR 0174 / core ADR-111): a
// loud banner while the boat is paused, and a quiet affordance while it is not.
//
// TWO SIZES ON PURPOSE. Paused is a state an owner must not be able to be in by
// accident or discover by scrolling, so it is the first thing on the page, in
// the page's loudest treatment, and it says what it means: nothing is being
// watched. Pausing, by contrast, is a rare deliberate act, so it asks quietly
// from the bottom of the same page and takes a confirm that states the truth
// before it does anything.
//
// NO OPTIMISTIC UI, anywhere. Both directions are read from `pausedAt` — the
// MIRROR of the boat's own row — never from the click. Between the tap and the
// boat's echo the control says "Sent to your boat…" and the banner stays exactly
// as it was. See the module header in `lib/alarmsPaused` for why this surface in
// particular can never be allowed to paint an outcome it does not have.
//
// The "in flight" claim is OWNED HERE, as request state, and it is ended by the
// echo or by a deadline — never inferred from "the mirror disagrees with what I
// sent". A boat resumed at the helm hours later reproduces that mismatch
// exactly, and a page nobody has touched would start announcing a command on
// its way.
//
// The confirm is an inline panel rather than `window.confirm`: it has to state
// what stops (naming the emergency alarms, which are not excepted), what happens
// to an alarm that is sounding right now AND what to do when one cannot clear,
// and what comes back — and a native dialog can neither be styled to carry that
// nor asserted in a test.

import { useEffect, useState } from 'react';

import { BOAT_ECHO_TIMEOUT_MS } from '../../lib/boatEcho';
import {
  ALARMS_PAUSED_HEADLINE,
  PAUSE_AFFORDANCE_QUESTION,
  PAUSE_CONFIRM_CANCEL,
  PAUSE_CONFIRM_COMMIT,
  PAUSE_CONFIRM_IN_FLIGHT,
  PAUSE_CONFIRM_RESTORE,
  PAUSE_CONFIRM_SCOPE,
  PAUSE_CONFIRM_STUCK_REMEDY,
  PAUSE_CONFIRM_TITLE,
  PAUSE_LABEL,
  PAUSE_OFFLINE_REASON,
  RESUME_LABEL,
  RESUME_OFFLINE_REASON,
  alarmsPauseStatusText,
  isAlarmsPaused,
  pauseConfirmActiveLine,
  pausedSinceLabel,
  sendAlarmsPause,
  settleAlarmsPause,
  timeOutAlarmsPause,
  type AlarmsPauseSaveState,
} from '../../lib/alarmsPaused';

interface AlarmsPausedBannerProps {
  boatId: string;
  /** The MIRROR — the boat's own answer. `null`/absent = watching. */
  pausedAt: string | null | undefined;
  /**
   * Presence. An offline boat still renders the banner (the pause is a fact
   * about the boat, and it is TRUE while the boat is offline), but cannot be
   * told anything, so the buttons are disabled with a VISIBLE reason rather
   * than left live to 409.
   */
  boatOnline: boolean;
  /**
   * How many rows the ACTIVE band is showing — the confirm's count. `null` when
   * the cloud has mirrored no runtime for this boat, which is NOT the same as
   * zero and must not be shown as "nothing is sounding".
   */
  activeCount: number | null;
  /** Re-read the alarm state so the boat's echo lands without a reload. */
  onSent: () => void;
}

export function AlarmsPausedBanner({
  boatId,
  pausedAt,
  boatOnline,
  activeCount,
  onSent,
}: AlarmsPausedBannerProps) {
  // A Try it guest pauses and resumes its own slot's alarms too (TOM-914, cloud ADR 0444).
  const [save, setSave] = useState<AlarmsPauseSaveState>({ kind: 'idle' });
  const [confirming, setConfirming] = useState(false);
  const paused = isAlarmsPaused(pausedAt);
  const busy = save.kind === 'sending';
  const status = alarmsPauseStatusText(save);

  // The boat's echo ends the in-flight request — the ONLY success signal this
  // control accepts. `settleAlarmsPause` returns the same object when nothing
  // changed, so this cannot loop.
  useEffect(() => {
    setSave((prev) => settleAlarmsPause(prev, paused));
  }, [paused]);

  // …and a deadline ends it when no echo ever comes, so the page stops claiming
  // a command is moving and says what is actually true instead.
  useEffect(() => {
    if (save.kind !== 'sent') return;
    const id = window.setTimeout(
      () => setSave((prev) => timeOutAlarmsPause(prev)),
      BOAT_ECHO_TIMEOUT_MS
    );
    return () => window.clearTimeout(id);
  }, [save]);

  async function send(next: boolean): Promise<void> {
    if (busy) return;
    setConfirming(false);
    setSave({ kind: 'sending', paused: next });
    const outcome = await sendAlarmsPause(boatId, next);
    setSave(outcome);
    if (outcome.kind === 'sent') onSent();
  }

  // Rendered as text, under the disabled button. See the constants' comment:
  // for a laid-up boat this is the ordinary case, not an edge, and a tooltip is
  // not an answer on the phone most of these visits come from.
  const offlineReason = boatOnline ? null : paused ? RESUME_OFFLINE_REASON : PAUSE_OFFLINE_REASON;

  if (paused) {
    return (
      <section className="alarm-band alarms-paused" data-testid="alarms-paused-banner" role="alert">
        <div className="alarms-paused__head">
          <h2 className="alarms-paused__headline">{ALARMS_PAUSED_HEADLINE}</h2>
          <p className="alarms-paused__since" data-testid="alarms-paused-since">
            {pausedSinceLabel(pausedAt)}
          </p>
        </div>
        <div className="alarms-paused__actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !boatOnline}
            data-testid="alarms-resume-btn"
            onClick={() => void send(false)}
          >
            {busy ? 'Sending…' : RESUME_LABEL}
          </button>
        </div>
        {offlineReason !== null ? (
          <p className="alarms-paused__offline" data-testid="alarms-pause-offline-reason">
            {offlineReason}
          </p>
        ) : null}
        {status !== null ? (
          <p
            className={`alarms-paused__status${statusIsWarn(save) ? ' alarms-paused__status--warn' : ''}`}
            role="status"
            aria-live="polite"
            data-testid="alarms-pause-status"
          >
            {status}
          </p>
        ) : null}
      </section>
    );
  }

  const activeLine = pauseConfirmActiveLine(activeCount);

  return (
    <section className="alarm-pause-offer" data-testid="alarms-pause-offer">
      {confirming ? (
        <div className="alarm-pause-confirm" data-testid="alarms-pause-confirm">
          <h3 className="alarm-pause-confirm__title">{PAUSE_CONFIRM_TITLE}</h3>
          <p>{PAUSE_CONFIRM_SCOPE}</p>
          {activeLine !== null ? (
            <p data-testid="alarms-pause-confirm-active">
              {activeLine} {PAUSE_CONFIRM_IN_FLIGHT} {PAUSE_CONFIRM_STUCK_REMEDY}
            </p>
          ) : null}
          <p>{PAUSE_CONFIRM_RESTORE}</p>
          <div className="alarm-pause-confirm__actions">
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="alarms-pause-cancel"
              onClick={() => setConfirming(false)}
            >
              {PAUSE_CONFIRM_CANCEL}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy || !boatOnline}
              data-testid="alarms-pause-confirm-btn"
              onClick={() => void send(true)}
            >
              {PAUSE_CONFIRM_COMMIT}
            </button>
          </div>
        </div>
      ) : (
        <div className="alarm-pause-offer__row">
          <span className="alarm-pause-offer__q">{PAUSE_AFFORDANCE_QUESTION}</span>
          <button
            type="button"
            className="alarm-pause-offer__btn"
            disabled={busy || !boatOnline}
            data-testid="alarms-pause-btn"
            onClick={() => setConfirming(true)}
          >
            {busy ? 'Sending…' : PAUSE_LABEL}
          </button>
        </div>
      )}
      {offlineReason !== null ? (
        <p className="alarm-pause-offer__offline" data-testid="alarms-pause-offline-reason">
          {offlineReason}
        </p>
      ) : null}
      {status !== null ? (
        <p
          className={`alarm-pause-offer__status${statusIsWarn(save) ? ' alarm-pause-offer__status--warn' : ''}`}
          role="status"
          aria-live="polite"
          data-testid="alarms-pause-status"
        >
          {status}
        </p>
      ) : null}
    </section>
  );
}

/** The states whose line is bad news rather than progress. */
function statusIsWarn(save: AlarmsPauseSaveState): boolean {
  return save.kind === 'error' || save.kind === 'offline' || save.kind === 'unconfirmed';
}
